import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  IconButton,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import { VideoCall, Delete, Refresh } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { KYCLevel2, KYCStatus } from '../types/kyc';
import { validateFile, FILE_CONSTRAINTS, ipfsUriToUrl } from '../utils/ipfs';

interface KYCLevel2FormProps {
   KYCStatus?:KYCStatus;
  initialData?: KYCLevel2;
  onSubmit: (data: KYCLevel2, files: { videoFile: File | null }) => Promise<void>;
  submitting: boolean;
}

// Yup validation rules
const validationSchema = Yup.object({
  randomNumber: Yup.string()
    .required('Please generate a random number')
    .matches(/^\d{6}$/, 'Random number must be 6 digits'),
  videoUrl: Yup.string()
    .required('Please upload the verification video'),
});

const KYCLevel2Form: React.FC<KYCLevel2FormProps> = ({
   KYCStatus,
  initialData,
  onSubmit,
  submitting
}) => {
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string>('');
  const [generatedNumber, setGeneratedNumber] = useState<string>('');

  // 初始化Formik
  const formik = useFormik({
    initialValues: {
      randomNumber: '',
      videoUrl: '',
    },
    validationSchema,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await onSubmit(values, { videoFile });
      } catch (error) {
        setFieldError('submit', error instanceof Error ? error.message : 'Submission failed');
      }
    },
  });

  useEffect(() => {
    if (initialData) {
      formik.setValues({
        randomNumber: initialData.randomNumber || '',
        videoUrl: initialData.videoUrl || ''
      });
      // If there is an initial video URL, set the preview
      if (initialData.videoUrl) {
        setVideoPreview(ipfsUriToUrl(initialData.videoUrl)); 
      }
    }
    // Generate initial random number
    generateRandomNumber();
  }, [initialData]);

  // Clean up preview URL
  useEffect(() => {
    return () => {
      if (videoPreview && videoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(videoPreview);
      }
    };
  }, []);

  // Create preview URL
  const createPreviewUrl = (file: File): string => {
    return URL.createObjectURL(file);
  };

  // Clean up preview URL
  const cleanupPreviewUrl = (url: string) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  const generateRandomNumber = () => {
    // Generate a 6-digit random number
    const randomNum = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedNumber(randomNum);
    formik.setFieldValue('randomNumber', randomNum);
    
    // Clear related errors
    if (formik.errors.randomNumber) {
      formik.setFieldError('randomNumber', '');
    }
  };

  const handleVideoSelect = (file: File) => {
    // Validate file
    const validationError = validateFile(file, FILE_CONSTRAINTS.VIDEO.types, FILE_CONSTRAINTS.VIDEO.maxSize);
    if (validationError) {
      formik.setFieldError('videoUrl', validationError);
      return;
    }

    // Check if it is a video file
    if (!file.type.startsWith('video/')) {
      formik.setFieldError('videoUrl', 'Please upload a video file');
      return;
    }

    // Check file size (limit to 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      formik.setFieldError('videoUrl', 'Video file size cannot exceed 50MB');
      return;
    }

    // Clean up old preview URL
    if (videoPreview) {
      cleanupPreviewUrl(videoPreview);
    }

    // Create a new preview URL
    const previewUrl = createPreviewUrl(file);
    setVideoPreview(previewUrl);
    setVideoFile(file);
    formik.setFieldValue('videoUrl', 'selected'); // Mark file as selected
    
    // Clear errors
    formik.setFieldError('videoUrl', '');
  };



  const handleRemoveVideo = () => {
    // 清理预览URL
    if (videoPreview) {
      cleanupPreviewUrl(videoPreview);
    }
    
    setVideoPreview('');
    setVideoFile(null);
    formik.setFieldValue('videoUrl', '');
  };



  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Card>
      <CardContent>
        <form onSubmit={formik.handleSubmit}>
          <Typography variant="h6" gutterBottom>
            Video Verification
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            Please record a video and clearly say the random numbers displayed below for advanced identity verification.
          </Alert>
          
          {(formik.errors as any).submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(formik.errors as any).submit}
            </Alert>
          )}
          
          {submitting && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Submitting...
              </Typography>
            </Box>
          )}
        
        <Grid container spacing={3}>
          {/* Random number display and generation */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ p: 2, bgcolor: 'primary.50' }}>
              <Typography variant="subtitle1" gutterBottom>
                Please say the following numbers in the video:
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip
                  label={generatedNumber || 'Click to generate a random number'}
                  color="primary"
                  size="medium"
                  sx={{ fontSize: '1.2rem', fontWeight: 'bold', px: 2, py: 1 }}
                />
                
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={generateRandomNumber}
                  disabled={submitting}
                  size="small"
                >
                  Regenerate
                </Button>
              </Box>
              
              <Typography variant="body2" color="text.secondary">
                Hint: Please make sure to say these 6 digits clearly and completely in the video.
              </Typography>
              
              {formik.errors.randomNumber && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {formik.errors.randomNumber}
                </Typography>
              )}
            </Card>
          </Grid>
          
          {/* Hidden random number input field (for form data) */}
          <Grid item xs={12} sx={{ display: 'none' }}>
            <TextField
              name="randomNumber"
              value={formik.values.randomNumber || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
            />
          </Grid>
          
          {/* Video Upload */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Verification Video
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Video Requirements:</strong>
                  <br />• Duration: 10-60 seconds
                  <br />• Format: MP4, MOV, AVI, and other common video formats
                  <br />• Size: No more than 50MB
                  <br />• Content: Clearly say the 6-digit random number displayed above
                  <br />• Environment: Well-lit, clear background
                </Typography>
              </Alert>
              
              {(formik.values.videoUrl || videoPreview) ? (
                <Box>
                  <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body1" gutterBottom>
                          ✅ Video selected
                        </Typography>
                        {videoFile && (
                          <Typography variant="body2" color="text.secondary">
                            File name: {videoFile.name}
                            <br />
                            Size: {formatFileSize(videoFile.size)}
                          </Typography>
                        )}
                      </Box>
                      
                      <IconButton
                        color="error"
                        onClick={handleRemoveVideo}
                        disabled={submitting}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                    
                    {/* Video Preview */}
                    <Box sx={{ mt: 2 }}>
                      <video
                        src={videoPreview || formik.values.videoUrl}
                        controls
                        style={{ width: '100%', maxWidth: 400, height: 'auto',maxHeight:300}}
                      />
                    </Box>
                  </Card>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                  <VideoCall sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  
                  <Typography variant="body1" gutterBottom>
                    Upload Verification Video
                  </Typography>
                  
                  <Button
                    variant="contained"
                    component="label"
                    disabled={submitting}
                    sx={{ mt: 1 }}
                  >
                    Select Video File
                    <input
                      type="file"
                      hidden
                      accept="video/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleVideoSelect(file);
                      }}
                    />
                  </Button>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Supports MP4, MOV, AVI, etc., up to 50MB
                  </Typography>
                </Box>
              )}
              
              {formik.errors.videoUrl && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {formik.errors.videoUrl}
                </Typography>
              )}
            </Box>
          </Grid>
          
          {/* Submit Button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting || !formik.values.randomNumber || !formik.values.videoUrl || !formik.isValid||KYCStatus==2||KYCStatus==3}
              >
                {submitting ? 'Submitting...' : 'Submit Video Verification'}
              </Button>
            </Box>
          </Grid>
        </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

export default KYCLevel2Form;